import React, { useState, useEffect } from 'react';
import { 
  RoomTemplate, 
  WeeklyTemplate, 
  createTemplateFromConfig,
  generateDefaultTemplates,
  createCustomTemplate,
  getTimeSlots,
  calculateWeeklyHours,
  DEFAULT_TEMPLATE_CONFIGS
} from '../utils/roomTemplateUtils';

interface RoomTemplateBuilderProps {
  onTemplateSelect: (template: RoomTemplate) => void;
  onTemplateSave?: (template: RoomTemplate) => void;
  initialTemplate?: RoomTemplate;
  showSaveOption?: boolean;
  applyImmediately?: boolean; // If true, applies template immediately. If false, just selects it.
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
] as const;

const RoomTemplateBuilder: React.FC<RoomTemplateBuilderProps> = ({
  onTemplateSelect,
  onTemplateSave,
  initialTemplate,
  showSaveOption = false,
  applyImmediately = true
}) => {
  const [selectedBaseTemplate, setSelectedBaseTemplate] = useState<string>('business-hours');
  const [customName, setCustomName] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customSchedule, setCustomSchedule] = useState<WeeklyTemplate>({
    monday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
    tuesday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
    wednesday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
    thursday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
    friday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
    saturday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] },
    sunday: { is_available: false, intervals: [{ start_time: '09:00', end_time: '17:00' }] }
  });
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [timeSlots] = useState(getTimeSlots());

  const defaultTemplates = generateDefaultTemplates();

  useEffect(() => {
    if (initialTemplate) {
      setCustomName(initialTemplate.name);
      setCustomDescription(initialTemplate.description);
      setCustomSchedule(initialTemplate.schedule);
      setIsCustomMode(true);
    }
  }, [initialTemplate]);

  const handleBaseTemplateChange = (templateKey: string) => {
    setSelectedBaseTemplate(templateKey);
    const template = defaultTemplates[templateKey];
    if (template) {
      setCustomName(template.name);
      setCustomDescription(template.description);
      setCustomSchedule(template.schedule);
    }
  };

  const handleDayToggle = (day: keyof WeeklyTemplate) => {
    setCustomSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        is_available: !prev[day].is_available
      }
    }));
  };

  const handleTimeChange = (
    day: keyof WeeklyTemplate,
    intervalIndex: number,
    field: 'start_time' | 'end_time',
    value: string
  ) => {
    setCustomSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        intervals: prev[day].intervals.map((interval, index) => 
          index === intervalIndex 
            ? { ...interval, [field]: value }
            : interval
        )
      }
    }));
  };

  const addTimeInterval = (day: keyof WeeklyTemplate) => {
    setCustomSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        intervals: [
          ...prev[day].intervals,
          { start_time: '09:00', end_time: '17:00' }
        ]
      }
    }));
  };

  const removeTimeInterval = (day: keyof WeeklyTemplate, intervalIndex: number) => {
    setCustomSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        intervals: prev[day].intervals.filter((_, index) => index !== intervalIndex)
      }
    }));
  };

  const generateTemplate = (): RoomTemplate => {
    if (isCustomMode) {
      return createCustomTemplate(customName, customDescription, customSchedule);
    } else {
      return createTemplateFromConfig(selectedBaseTemplate as keyof typeof DEFAULT_TEMPLATE_CONFIGS, {
        name: customName,
        description: customDescription
      });
    }
  };

  const handleApplyTemplate = () => {
    const template = generateTemplate();
    onTemplateSelect(template);
  };

  const handleSaveTemplate = () => {
    if (onTemplateSave) {
      const template = generateTemplate();
      onTemplateSave(template);
    }
  };

  const currentTemplate = generateTemplate();
  const weeklyHours = calculateWeeklyHours(currentTemplate);

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Plantilla Base
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(defaultTemplates).map(([key, template]) => (
            <button
              key={key}
              onClick={() => handleBaseTemplateChange(key)}
              className={`p-3 text-left border rounded-lg transition-colors ${
                selectedBaseTemplate === key && !isCustomMode
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-sm">{template.name}</div>
              <div className="text-xs text-gray-600">{template.description}</div>
            </button>
          ))}
          <button
            onClick={() => setIsCustomMode(true)}
            className={`p-3 text-left border rounded-lg transition-colors ${
              isCustomMode
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="font-medium text-sm">Personalizado</div>
            <div className="text-xs text-gray-600">Crear desde cero</div>
          </button>
        </div>
      </div>

      {/* Template Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre de la Plantilla
          </label>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ej: Horario Personalizado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <input
            type="text"
            value={customDescription}
            onChange={(e) => setCustomDescription(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ej: Horario especial para eventos"
          />
        </div>
      </div>

      {/* Weekly Schedule */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Horario Semanal</h3>
          <div className="text-sm text-gray-600">
            Total: {weeklyHours} horas/semana
          </div>
        </div>
        
        <div className="space-y-3">
          {DAYS_OF_WEEK.map(({ key, label }) => {
            const daySchedule = customSchedule[key];
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={daySchedule.is_available}
                      onChange={() => handleDayToggle(key)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium text-gray-900">{label}</span>
                  </label>
                  {daySchedule.is_available && (
                    <button
                      onClick={() => addTimeInterval(key)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Agregar intervalo
                    </button>
                  )}
                </div>
                
                {daySchedule.is_available && (
                  <div className="space-y-2">
                    {daySchedule.intervals.map((interval, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <select
                          value={interval.start_time}
                          onChange={(e) => handleTimeChange(key, index, 'start_time', e.target.value)}
                          className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {timeSlots.map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                        <span className="text-gray-500">-</span>
                        <select
                          value={interval.end_time}
                          onChange={(e) => handleTimeChange(key, index, 'end_time', e.target.value)}
                          className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {timeSlots.map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                        {daySchedule.intervals.length > 1 && (
                          <button
                            onClick={() => removeTimeInterval(key, index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        {showSaveOption && onTemplateSave && (
          <button
            onClick={handleSaveTemplate}
            disabled={!customName.trim()}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Guardar Plantilla
          </button>
        )}
        <button
          onClick={handleApplyTemplate}
          disabled={!customName.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {applyImmediately ? 'Aplicar Plantilla' : 'Seleccionar Plantilla'}
        </button>
      </div>
    </div>
  );
};

export default RoomTemplateBuilder;
